import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Button, Typography, Divider, CircularProgress } from '@mui/material';
import { AppDispatch } from '../reduxStore/Store';
import { fetchStatVentes, clearStatVentes } from '../reduxStore/StatistiqueSlice';
import {
  SysTypeDocument, Tiers, Article, FamilleData, GeneriqueData, Departement,
  StatVentesArticle, StatVentesMois, StatVentesTiers,
} from '../models/models';
import { GeneriqueDataType, PERMISSION_PRIX_REVIENT, TypeTiers } from '../constants/enums';
import { usePermission } from '../hooks/usePermission';
import { TableHeader } from '../constants/interfaces';
import { useSnackbar } from './SnackbarProvider';
import AppSurface from './layout/AppSurface';
import MyTable from './MyTable';
import SelectSysTypeDocument from './Selects/SelectSysTypeDocument';
import SelectDepot from './Selects/SelectDepot';
import SelectDepartement from './Selects/SelectDepartement';
import ClientAutoC from './AutoComplete/ClientAutoC';
import FournisseurAutoC from './AutoComplete/FournisseurAutoC';
import ArticleAutoC from './AutoComplete/ArticleAutoC';
import FamilleAutoC from './AutoComplete/FamilleAutoC';

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('sv-SE');
};
const today = () => new Date().toLocaleDateString('sv-SE');
const fmt = (n: number) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Décale une date 'YYYY-MM-DD' de n années (borne les sélecteurs de date).
const addYears = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y + n, m - 1, d).toLocaleDateString('sv-SE');
};
// La période dépasse-t-elle 1 an ? (max autorisé)
const periodeTropLongue = (debut: string, fin: string) =>
  (Date.parse(fin) - Date.parse(debut)) / 86_400_000 > 366;

// Colonnes des 3 tableaux de détail (MyTable → pagination client intégrée).
const headersArticle: TableHeader<StatVentesArticle>[] = [
  { key: 'article_code',        label: 'Code' },
  { key: 'article_designation', label: 'Article' },
  { key: 'quantite', label: 'Quantité', align: 'right', render: (r) => fmt(r.quantite) },
  { key: 'ca',       label: 'CA HT',    align: 'right', render: (r) => fmt(r.ca) },
  { key: 'ca_ttc',   label: 'CA TTC',   align: 'right', render: (r) => fmt(r.ca_ttc), hideBelow: 'md' },
  { key: 'cout',     label: 'Coût HT',  align: 'right', render: (r) => fmt(r.cout) },
  { key: 'marge',    label: 'Marge',    align: 'right', render: (r) => fmt(r.marge) },
];
const headersMois: TableHeader<StatVentesMois>[] = [
  { key: 'mois',  label: 'Mois' },
  { key: 'ca',     label: 'CA HT',     align: 'right', render: (r) => fmt(r.ca) },
  { key: 'ca_ttc', label: 'CA TTC',    align: 'right', render: (r) => fmt(r.ca_ttc), hideBelow: 'md' },
  { key: 'cout',   label: 'Coût HT',   align: 'right', render: (r) => fmt(r.cout) },
  { key: 'marge', label: 'Marge',     align: 'right', render: (r) => fmt(r.marge) },
  { key: 'nb_documents', label: 'Documents', align: 'right' },
];
const headersTiers: TableHeader<StatVentesTiers>[] = [
  { key: 'tiers_nom', label: 'Tiers', render: (r) => `${r.tiers_nom ?? ''} ${r.tiers_prenom ?? ''}`.trim() || '—' },
  { key: 'ca',     label: 'CA HT',   align: 'right', render: (r) => fmt(r.ca) },
  { key: 'ca_ttc', label: 'CA TTC',  align: 'right', render: (r) => fmt(r.ca_ttc), hideBelow: 'md' },
  { key: 'cout',   label: 'Coût HT', align: 'right', render: (r) => fmt(r.cout) },
  { key: 'marge', label: 'Marge', align: 'right', render: (r) => fmt(r.marge) },
  { key: 'nb_documents', label: 'Documents', align: 'right' },
];

// Sans le droit de voir le coût (0156) : l'API renvoie null → colonnes Coût / Marge retirées
const COLONNES_COUT = ['cout', 'marge'];
const sansCout = <T,>(headers: TableHeader<T>[], voitCout: boolean): TableHeader<T>[] =>
  voitCout ? headers : headers.filter((h) => !COLONNES_COUT.includes(String(h.key)));

/**
 * Stats de vente — panneau embarquable (onglet de Stock & Statistiques).
 * Filtres : type de document, tiers, article, famille, dépôt, département
 * (département visible uniquement en mode « tous départements »).
 */
const StatVentesPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { data, loading, error } = useSelector((state: any) => state.StatistiqueSlice.statVentes);
  // Mode « tous départements » = aucun département actif dans le contexte global.
  const selectedDepartementGlobal = useSelector((state: any) => state.AppSlice.selectedDepartement);
  const isAllDept = !selectedDepartementGlobal;
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);

  // Filtres
  const [typeDoc, setTypeDoc]   = useState<SysTypeDocument | null>(null);
  const [tiers, setTiers]       = useState<Tiers | null>(null);
  const [article, setArticle]   = useState<Article | null>(null);
  const [famille, setFamille]   = useState<FamilleData | null>(null);
  const [depot, setDepot]       = useState<GeneriqueData | null>(null);
  const [departement, setDepartement] = useState<Departement | null>(null);
  const [dateDebut, setDateDebut] = useState(firstOfMonth());
  const [dateFin, setDateFin]     = useState(today());

  const charger = () => {
    if (periodeTropLongue(dateDebut, dateFin)) {
      showSnackbar('La période ne peut pas dépasser 1 an.', 'error');
      return;
    }
    dispatch(fetchStatVentes({
      type_doc_code:  typeDoc?.code ?? null,
      tiers_id:       tiers?.id ?? null,
      article_id:     article?.id ?? null,
      famille_id:     famille?.id ?? null,
      depot_id:       depot?.id ?? null,
      departement_id: isAllDept ? (departement?.id ?? null) : null,
      date_debut:     dateDebut,
      date_fin:       dateFin,
    }));
  };

  useEffect(() => {
    charger();
    return () => { dispatch(clearStatVentes()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showSnackbar(error, 'error');
  }, [error, showSnackbar]);

  const synthese = data?.synthese;
  const isFournisseur = typeDoc?.type_tiers === TypeTiers.FOURNISSEUR;

  return (
    <>
      {/* ── Filtres ── */}
      <AppSurface sx={{ mb: 2 }}>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} alignItems="start">
          <SelectSysTypeDocument
            showAll
            selectedSysType={typeDoc}
            onSysTypeChange={(t) => { setTypeDoc(t); setTiers(null); }}
          />
          {isFournisseur ? (
            <FournisseurAutoC selectedFournisseur={tiers} onFournisseurChange={setTiers} />
          ) : (
            <ClientAutoC selectedClient={tiers} onClientChange={setTiers} />
          )}
          <ArticleAutoC selectedArticle={article} onArticleChange={setArticle} />
          <FamilleAutoC
            slot="main"
            type={GeneriqueDataType.FAMILLE_ARTICLE}
            label="Famille"
            selectedFamille={famille}
            onFamilleChange={setFamille}
          />
          <SelectDepot selectedDepot={depot} onDepotChange={setDepot} />
          {isAllDept && (
            <SelectDepartement
              allowAll
              selectedDepartement={departement}
              onDepartementChange={setDepartement}
            />
          )}
          <TextField
            type="date" label="Du" value={dateDebut}
            onChange={(e) => setDateDebut(e.target.value)}
            size="small" InputLabelProps={{ shrink: true }}
            inputProps={{ min: addYears(dateFin, -1), max: dateFin }}
          />
          <TextField
            type="date" label="Au" value={dateFin}
            onChange={(e) => setDateFin(e.target.value)}
            size="small" InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateDebut, max: addYears(dateDebut, 1) }}
          />
          <Box display="flex" alignItems="center">
            <Button variant="contained" onClick={charger} disabled={loading}>Calculer</Button>
          </Box>
        </Box>
      </AppSurface>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      )}

      {!loading && data && (
        <>
          {/* ── Synthèse ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Synthèse</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box display="grid" gridTemplateColumns={{ xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }} gap={1} textAlign="center">
              {[
                ['Chiffre d\'affaires HT', fmt(synthese.ca)],
                ['Chiffre d\'affaires TTC', fmt(synthese.ca_ttc)],
                ...(voitCout ? [['Coût HT', fmt(synthese.cout)], ['Marge HT', fmt(synthese.marge)]] : []),
                ['Quantité vendue', fmt(synthese.quantite)],
                ['Documents', String(synthese.nb_documents)],
              ].map(([label, val]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6">{val}</Typography>
                </Box>
              ))}
            </Box>
          </AppSurface>

          {/* ── Par article ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Par article</Typography>
            <Divider sx={{ mb: 1 }} />
            <MyTable
              headers={sansCout(headersArticle, voitCout)}
              data={data.parArticle}
              totalRows={data.parArticle.length}
              defaultPageSize={10}
              size="small"
              tableKey="stat-ventes-article"
              totals={{
                quantite: fmt(synthese.quantite),
                ca:       fmt(synthese.ca),
                ca_ttc:   fmt(synthese.ca_ttc),
                cout:     fmt(synthese.cout),
                marge:    fmt(synthese.marge),
              }}
            />
          </AppSurface>

          {/* ── Par mois ── */}
          <AppSurface sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Par mois</Typography>
            <Divider sx={{ mb: 1 }} />
            <MyTable
              headers={sansCout(headersMois, voitCout)}
              data={data.parMois}
              totalRows={data.parMois.length}
              defaultPageSize={12}
              size="small"
              totals={{
                ca:           fmt(synthese.ca),
                ca_ttc:       fmt(synthese.ca_ttc),
                cout:         fmt(synthese.cout),
                marge:        fmt(synthese.marge),
                nb_documents: String(synthese.nb_documents),
              }}
            />
          </AppSurface>

          {/* ── Par tiers ── */}
          <AppSurface>
            <Typography variant="h6" gutterBottom>Par tiers</Typography>
            <Divider sx={{ mb: 1 }} />
            <MyTable
              headers={sansCout(headersTiers, voitCout)}
              data={data.parTiers}
              totalRows={data.parTiers.length}
              defaultPageSize={10}
              size="small"
              tableKey="stat-ventes-tiers"
              totals={{
                ca:           fmt(synthese.ca),
                ca_ttc:       fmt(synthese.ca_ttc),
                cout:         fmt(synthese.cout),
                marge:        fmt(synthese.marge),
                nb_documents: String(synthese.nb_documents),
              }}
            />
          </AppSurface>
        </>
      )}
    </>
  );
};

export default StatVentesPanel;
